import React, { useContext, useEffect, useState, useMemo } from 'react';
import _ from 'lodash';
import { ArrowLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import LoadingSpinner from '../../../../components/LoadingSpinner';
import { defaultDarValue, documentKeys, documentNames, documentReferences, overrideDefaults } from './utils/data';
import { FormContext } from '../context/FormContextWrapper';
import { useAuth } from '../../../context/wms-context';
import {
    addOverridesToFormData,
    getDataForDocuments,
    printGeneratedDocuments,
    submitUpdatedHeaderData,
    getInfoForBigATAExcel,
    updateBigAtaLinesInSF,
    resolveAccountIdsForBigAtaRows,
    buildBigAtaSfPatches,
    buildCustomAsaForBigATA,
} from './utils/functions';
import { GuidedInterfaceContext } from '../context/GuidedInterfaceContext';
import Document8130Form from './formOverrides/Document8130Form';
import ASADocumentForm from './formOverrides/ASADocumentForm';
import BoeingDocumentForm from './formOverrides/BoeingDocumentForm';
import PackingSlipForm from './formOverrides/PackingSlipForm';
import { updateReleaseInspectionStatus } from '../utils/guided-interface-functions';
import logError from '../../../../utils/errorUtil';

import EditableBigAtaTableModal from '../components/EditableBigAtaTableModal';
import ConfirmModal from '../components/ConfirmModal';

export default function Forms({ currentReleaseLine, releaseData, releaseName, returnToReleaseLines, releaseLineData }) {
    const { currentUser } = useAuth();

    const {
        documentDefaults,
        updateDocumentDefaults,
        packingSheetLastPrinted,
        documentInfo,
        setDocumentInfo,
        selectedDocuments,
        setSelectedDocuments,
    } = useContext(FormContext);
    const { partInspectionSeconds, partInspectionTimeDisplay, setShowNotification, startTimer, stopTimer } =
        useContext(GuidedInterfaceContext);

    const [formOverrides, setFormOverrides] = useState({ ...documentDefaults });
    const [currentForm, setCurrentForm] = useState(null);
    const [loading, setLoading] = useState(true);
    const [acceptButtonLoading, setAcceptButtonLoading] = useState(false);
    const [printButtonLoading, setPrintButtonLoading] = useState(false);
    const [persistedDocumentInfo, setPersistedDocumentInfo] = useState({});
    const [requiredDocuments, setRequiredDocuments] = useState(
        releaseData.isRepairOrderRelease
            ? [documentReferences.PACKING_SLIP_UNPRICED]
            : [documentReferences.DOCUMENT_ASA, documentReferences.PACKING_SLIP_UNPRICED]
    );
    const [nonRequiredDocuments, setNonRequiredDocuments] = useState([]);
    const [darPrinted, setDarPrinted] = useState(false);

    const isWillCall = releaseData.isWillCall;
    const isBoeingDropship = releaseData.isBoeingDropship;
    const isBoeingAccount = releaseData.isBoeingAccount;
    const isDAR = releaseData.isDar === 'Yes';
    const isRepairOrderRelease = releaseData.isRepairOrderRelease;
    const [conditionCodes, setConditionCodes] = useState([]);
    const [bigAtaConditionCodes, setBigAtaConditionCodes] = useState([]);
    const [ataModalData, setAtaModalData] = useState({});

    const [showBigATAModal, setShowBigATAModal] = useState(false);
    const [showConfirmPrintBigATA, setShowConfirmPrintBigATA] = useState(false);
    const [pendingPrintRows, setPendingPrintRows] = useState(null);

    const [accountOptions, setAccountOptions] = useState();

    let releaseId = releaseData.id;

    const bigAtaPrintableReleaseLines = useMemo(() => {
        return (releaseLineData ?? []).filter((releaseLine) => Boolean(releaseLine?.invLineId));
    }, [releaseLineData]);

    const getFormDataAndSetOverrides = async () => {
        try {
            setLoading(true);
            getAtaModalData();
            const response =
                documentInfo ??
                (await getDataForDocuments(releaseData.name, currentReleaseLine.releaseLineName, currentUser));

            let tempSelectedDocuments = [...selectedDocuments];
            const documentNames = Object.keys(documentKeys);

            response.previouslyPrintedDocuments?.forEach((documentData) => {
                const documentType = documentData.documentType;
                const isPackingSheetDocument =
                    documentType === documentKeys.PACKING_SLIP_PRICED || documentType === documentKeys.PACKING_SLIP_UNPRICED;
                const matchesReleaseLine = documentData.releaseLineNumber === currentReleaseLine.releaseLineName;
                const matchesReleaseNumber = documentData.releaseNumber === releaseData.name;

                const shouldSelectDocument = (isPackingSheetDocument && matchesReleaseNumber) || matchesReleaseLine;

                if (shouldSelectDocument) {
                    const documentKey = documentNames.find((name) => documentKeys[name] === documentType);

                    if (!documentKey) return;

                    const isAlreadySelected = isPackingSheetDocument
                        ? tempSelectedDocuments.some((doc) => doc.documentType === documentKey)
                        : tempSelectedDocuments.some(
                              (doc) =>
                                  doc.documentType === documentKey &&
                                  doc.releaseLineNumber === currentReleaseLine.releaseLineName
                          );

                    if (isAlreadySelected) return;

                    tempSelectedDocuments.push({
                        documentType: documentKey,
                        releaseLineNumber: currentReleaseLine.releaseLineName,
                    });
                }
            });

            handleRequiredDocuments();
            setSelectedDocuments(tempSelectedDocuments);
            setDocumentInfo(response);
            setConditionCodes(response.conditionCodes);
            setPersistedDocumentInfo(response.documentData);
            combineAllFormDataWithOverrides(response.documentData);

            setAccountOptions(response.accountOptions[0]);
            setBigAtaConditionCodes(response.accountOptions[1]);
        } catch (error) {
            setShowNotification({
                error: true,
                shown: true,
                title: 'Error Getting Data For Document(s)',
                message:
                    'There was an error generating data for documents. Please try again and if this issue persists contact your manager.',
            });
        }
        setLoading(false);
    };

    const handleRequiredDocuments = () => {
        if (releaseData.isRepairOrderRelease) {
            setNonRequiredDocuments([
                documentReferences.DOCUMENT_ASA,
                documentReferences.DOCUMENT_8130,
                documentReferences.DOCUMENT_BOEING,
                documentReferences.PACKING_SLIP_PRICED,
            ]);
            return;
        }

        const { DOCUMENT_8130, DOCUMENT_BOEING, PACKING_SLIP_PRICED } = documentReferences;
        const dynamicDocuments = [DOCUMENT_BOEING, DOCUMENT_8130, PACKING_SLIP_PRICED];

        let required = [];
        let nonRequired = [];
        for (const docName of dynamicDocuments) {
            switch (docName) {
                case PACKING_SLIP_PRICED:
                    {
                        if (releaseData.requirePackSheet && !isBoeingAccount) {
                            required.push(PACKING_SLIP_PRICED);
                            continue;
                        }
                        nonRequired.push(PACKING_SLIP_PRICED);
                    }
                    break;
                case DOCUMENT_BOEING:
                    {
                        if (isBoeingDropship) {
                            required.push(DOCUMENT_BOEING);
                            continue;
                        }
                        nonRequired.push(DOCUMENT_BOEING);
                    }
                    break;
                case DOCUMENT_8130: {
                    if (isDAR) {
                        required.push(DOCUMENT_8130);
                        continue;
                    }
                    nonRequired.push(DOCUMENT_8130);
                }
            }
        }

        setRequiredDocuments((docs) => {
            return [...docs, ...required];
        });
        setNonRequiredDocuments((docs) => {
            return [...docs, ...nonRequired];
        });
    };

    const combineAllFormDataWithOverrides = (documentData) => {
        const updatedFormOverrides = _.clone(formOverrides);

        // Go through each document
        for (const documentName in formOverrides) {
            const totalDocumentData = _.clone(documentData[documentName]);
            const updatedDocument = _.clone(
                totalDocumentData.find((data) => data.releaseLineNumber === currentReleaseLine.releaseLineName) ?? {}
            );
            const overrideDocument = _.clone(formOverrides[documentName]);
            const persistedDocumentOverrides = _.clone(overrideDefaults[documentName]);

            const isPackSheet =
                documentName === documentKeys.PACKING_SLIP_PRICED || documentName === documentKeys.PACKING_SLIP_UNPRICED;
            if (documentName === packingSheetLastPrinted) continue;
            if (isPackSheet && packingSheetLastPrinted) {
                const allPackingData = _.clone(documentData[packingSheetLastPrinted]);
                const persistedPackingDocument = _.clone(
                    allPackingData.find((data) => data.releaseLineNumber === currentReleaseLine.releaseLineName) ?? {}
                );
                // Go through each key to check for updates
                for (const override in updatedDocument) {
                    if (!(override in persistedDocumentOverrides)) continue;
                    if (persistedPackingDocument[override] !== overrideDocument[override]) {
                        overrideDocument[override] = persistedPackingDocument[override];
                    }
                }
                continue;
            }

            // Go through each key to check for updates
            for (const override in updatedDocument) {
                if (updatedDocument[override] !== overrideDocument[override]) {
                    overrideDocument[override] = updatedDocument[override];
                }
            }

            updatedFormOverrides[documentName] = overrideDocument;
        }

        setFormOverrides(updatedFormOverrides);
    };

    const dynamicallyParseFormData = (event, formName) => {
        const inputType = event.target.type;
        const keyName = event.target.name;
        const value = inputType === 'checkbox' ? event.target.checked : event.target.value;

        const overrides = { ...formOverrides };

        const isDarUpdate = keyName === 'darDisplayName' && formName === documentKeys.DOCUMENT_8130;

        if (isDarUpdate) {
            const darOptions = overrides.form8130.DAR;
            const selectedOption = darOptions?.find((dar) => dar.displayName === value) ?? defaultDarValue;
            const { authorizationName, authorizationNumber, displayName } = selectedOption;

            overrides.form8130.DAR = overrides.form8130.DAR.map((dar) => {
                if (dar.displayName === displayName) {
                    return { ...dar, priority: true };
                }
                return { ...dar, priority: false };
            });
            overrides.form8130.authorizationName = authorizationName;
            overrides.form8130.authorizationNumber = authorizationNumber;
            overrides.form8130.darDisplayName = displayName;
            return;
        }

        // Link both packing sheets together
        const isPackSheet = formName === documentKeys.PACKING_SLIP_PRICED || formName === documentKeys.PACKING_SLIP_UNPRICED;
        if (isPackSheet) {
            overrides[documentKeys.PACKING_SLIP_PRICED][keyName] = value;
            overrides[documentKeys.PACKING_SLIP_UNPRICED][keyName] = value;
            setFormOverrides(() => {
                return overrides;
            });
            return;
        }

        if (formName === 'form8130' && (keyName === 'showLeft' || keyName === 'showRight')) {
            if (keyName === 'showLeft' && overrides.form8130.showRight === true) {
                overrides.form8130.showLeft = true;
                overrides.form8130.showRight = false;
            } else if (keyName === 'showRight' && overrides.form8130.showLeft === true) {
                overrides.form8130.showLeft = false;
                overrides.form8130.showRight = true;
            }
            setFormOverrides(() => {
                return overrides;
            });
            return;
        }

        overrides[formName][keyName] = value;
        setFormOverrides(overrides);
    };

    const handleDocumentSelection = (documentKeyValue) => {
        setCurrentForm(documentKeyValue);
    };

    const formatDocumentsToPrint = () => {
        const documentsToPrint = {};

        if (currentForm in documentKeys) {
            const formKey = documentKeys[currentForm];

            // This was added to handle a feature regarding ASA or Boeing documents but there were merge conflicts
            // and the feature was not implemented, and not found out until later. This is a note to remove this at a later date if not resolved.
            // const asaOrBoeing = formKey === documentKeys.DOCUMENT_ASA || formKey === documentKeys.DOCUMENT_BOEING;
            // const correctDocumentInfo = asaOrBoeing ? documentInfo.documentData[formKey] : documentInfo[formKey];
            const correctDocumentInfo = documentInfo.documentData[formKey];

            const matchingDocuments = correctDocumentInfo.filter((data) => {
                if (formKey.includes('packingSheet')) return data; // Include all lines for packing sheets
                if (data.releaseLineNumber === currentReleaseLine.releaseLineName) return data;
            });

            documentsToPrint[formKey] = matchingDocuments;
        }

        return documentsToPrint;
    };

    // Compare previous pack sheet to the pack sheet submitted
    const getUpdatedPackingSheetData = () => {
        const updatedPackSheet = formOverrides?.packingSheetPriced || formOverrides?.packingSheetUnpriced;

        if (!updatedPackSheet) return;

        const originalPackSheet = persistedDocumentInfo?.packingSheetPriced?.find(
            (data) => data.releaseLineNumber === currentReleaseLine.releaseLineName
        );

        if (!originalPackSheet || !updatedPackSheet) return;

        const updatedPackSheetData = {
            awb: updatedPackSheet?.awb?.toString(),
            externalComments: updatedPackSheet?.externalComments?.toString(),
            shippedMethod: updatedPackSheet?.shippedMethod?.toString(),
            totalPieces: updatedPackSheet?.totalPieces?.toString(),
            totalWeight: updatedPackSheet?.totalWeight?.toString(),
        };
        const oldPackSheetData = {
            awb: originalPackSheet?.awb?.toString(),
            externalComments: originalPackSheet?.externalComments?.toString(),
            shippedMethod: originalPackSheet?.shippedMethod?.toString(),
            totalPieces: originalPackSheet?.totalPieces?.toString(),
            totalWeight: originalPackSheet?.totalWeight?.toString(),
        };

        const isPackingSheetTheSame = JSON.stringify(oldPackSheetData) === JSON.stringify(updatedPackSheetData);
        if (!isPackingSheetTheSame) return { ...updatedPackSheetData, releaseId: releaseId };
    };

    const handleFormPrint = async () => {
        setPrintButtonLoading(true);
        try {
            const updatedPackingSheetData = getUpdatedPackingSheetData();
            if (updatedPackingSheetData) {
                await submitUpdatedHeaderData(currentUser, updatedPackingSheetData);
            }

            const formattedDocuments = formatDocumentsToPrint();
            const updatedDocumentData = addOverridesToFormData(formOverrides, formattedDocuments);

            await printGeneratedDocuments(
                updatedDocumentData,
                currentReleaseLine.releaseLineName,
                releaseName,
                currentUser,
                releaseData.requirePackSheet,
                isWillCall
            );
            updateDocumentDefaults(updatedDocumentData, currentReleaseLine.releaseLineName);
            setSelectedDocuments([
                ...selectedDocuments,
                { documentType: currentForm, releaseLineNumber: currentReleaseLine.releaseLineName },
            ]);
            setCurrentForm(null);
        } catch (error) {
            setShowNotification({
                error: true,
                shown: true,
                title: 'Error Printing Document(s)',
                message:
                    'There was an error printing documents. Please try again and if this issue persists contact your manager.',
            });
        } finally {
            setPrintButtonLoading(false);
        }
    };

    const handleSaveInspectionStatus = async () => {
        setAcceptButtonLoading(true);
        const { statusResponse, timeResponse } = await updateReleaseInspectionStatus(
            'complete',
            'forms',
            currentReleaseLine.releaseLineName,
            releaseName,
            partInspectionSeconds,
            currentUser
        );
        if (statusResponse === 'success' && timeResponse === 'success') {
            setShowNotification({
                shown: true,
                status: 'success',
                title: 'Status Saved Successfully',
                message: 'Forms status was saved successfully.',
            });
        } else {
            setShowNotification({
                shown: true,
                status: 'error',
                title: 'Failed to Update Status',
                message: 'There was an error saving the status of the Forms page. Please try again.',
            });
        }

        setDarPrinted(false);
        returnToReleaseLines();
        setAcceptButtonLoading(false);
    };

    const handleBackButton = () => {
        returnToReleaseLines();
        setCurrentForm(null);
        setDarPrinted(false);
    };

    const handleSaveBigATA = (updatedRows) => {
        setAtaModalData((prev) => ({
            ...(prev ?? {}),
            editedRows: updatedRows,
            hasEdits: true,
        }));

        setPendingPrintRows(updatedRows);

        setShowConfirmPrintBigATA(true);
    };

    const handlePrintBigATA = async (rowsOverride) => {
        setPrintButtonLoading(true);
        try {
            const customAsaRow = buildCustomAsaForBigATA({ documentInfo, currentReleaseLine });

            const base =
                ataModalData && Object.keys(ataModalData).length
                    ? ataModalData
                    : await getInfoForBigATAExcel(bigAtaPrintableReleaseLines, currentUser);

            const rowsToPrint =
                rowsOverride ?? base?.editedRows ?? base?.originalRows ?? base?.releaseNumberData ?? base?.rows ?? [];

            // Ensure backend gets rows in the keys it expects
            const bigATADocumentData = {
                ...(base ?? {}),
                releaseNumberData: rowsToPrint,
                rows: rowsToPrint,
            };

            const documentsToPrint = {
                [documentKeys.DOCUMENT_ASA]: [customAsaRow],
                [documentKeys.DOCUMENT_BIG_ATA]: [bigATADocumentData],
            };

            await printGeneratedDocuments(
                documentsToPrint,
                currentReleaseLine.releaseLineName,
                releaseName,
                currentUser,
                releaseData.requirePackSheet,
                isWillCall
            );

            await getInfoForBigATAExcel(releaseLineData, releaseName, currentUser);
        } catch (error) {
            logError(error, 'handlePrintBigATA');
            setShowNotification({
                error: true,
                shown: true,
                title: 'Error Printing Custom ASA',
                message: 'There was an error printing the custom ASA document. Please try again.',
            });
        } finally {
            setPrintButtonLoading(false);
        }
    };

    const handleConfirmPrintYes = async () => {
        setPrintButtonLoading(true);
        try {
            const rowsToPrint = pendingPrintRows ?? ataModalData?.editedRows ?? ataModalData?.originalRows ?? [];

            const originalRows = ataModalData?.originalRows ?? ataModalData?.releaseNumberData ?? ataModalData?.rows ?? [];

            const rowsToPatch = resolveAccountIdsForBigAtaRows(rowsToPrint, accountOptions ?? []);
            const patches = buildBigAtaSfPatches(
                originalRows,
                rowsToPatch,
                accountOptions ?? [],
                bigAtaConditionCodes ?? []
            );

            if (patches.length) {
                const SF_FIELD_MAP = {
                    ConditionCodeId: 'inscor__Condition_Code__c',
                    TraceId: 'inscor__Trace__c',
                    TagAgencyId: 'inscor__Tag_Agency__c',
                    SerialNumber: 'inscor__Serial_Number__c',
                    inventoryLineId: 'Id',
                };

                const patchedWithSFFields = patches.map((patch) => {
                    const sfPatch = {};
                    for (const [key, value] of Object.entries(patch)) {
                        const sfKey = SF_FIELD_MAP[key];
                        if (sfKey) {
                            sfPatch[sfKey] = value;
                        }
                    }
                    return sfPatch;
                });

                await updateBigAtaLinesInSF(currentUser, patchedWithSFFields);
            }

            await handlePrintBigATA(rowsToPrint);

            setShowConfirmPrintBigATA(false);
            setPendingPrintRows(null);
            setShowBigATAModal(false);
        } catch (error) {
            logError(error, 'handleConfirmPrintYes');
            setShowNotification({
                shown: true,
                status: 'error',
                title: 'Error Printing Big ATA',
                message: 'There was an error printing the Big ATA. Please try again.',
            });
        } finally {
            setPrintButtonLoading(false);
        }
    };

    const handleAutoSaveBigATA = (updatedRows) => {
        setAtaModalData((prev) => ({
            ...(prev ?? {}),
            editedRows: updatedRows,
            hasEdits: true,
        }));

        setShowConfirmPrintBigATA(false);
        setShowBigATAModal(false);
    };

    const handleConfirmPrintNo = () => {
        setShowConfirmPrintBigATA(false);
        setPendingPrintRows(null);
    };

    async function getAtaModalData() {
        try {
            const result = await getInfoForBigATAExcel(bigAtaPrintableReleaseLines, currentUser);

            const originalRows = result?.releaseNumberData ?? result?.rows ?? [];

            setAtaModalData({
                ...(result ?? {}),
                originalRows,
                editedRows: originalRows,
                hasEdits: false,
            });
        } catch (error) {
            console.log(error);
            logError(error, 'getAtaModalData');
            setShowNotification({
                shown: true,
                status: 'error',
                title: 'Error Getting ATA Modal Data',
                message: 'There was an error getting the ATA modal data. Please try again.',
            });
        }
    }

    useEffect(() => {
        getFormDataAndSetOverrides();
    }, []);

    useEffect(() => {
        if (
            selectedDocuments.find(
                (document) =>
                    document.documentType === documentReferences.DOCUMENT_8130 &&
                    document.releaseLineNumber === currentReleaseLine.releaseLineName
            )
        ) {
            setDarPrinted(true);
        }
    }, [selectedDocuments]);

    useEffect(() => {
        if (isDAR && !isRepairOrderRelease) {
            window.addEventListener('beforeunload', handleBeforeUnload);
        }
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [releaseData, isRepairOrderRelease]);

    function handleBeforeUnload(e) {
        e.preventDefault();
        e.returnValue = 'The release is a DAR. Do you really want to leave?';
    }

    return (
        <section className='flex flex-col gap-y-8 justify-start items-center px-8 pt-10 pb-20 h-full'>
            <header className='flex flex-col justify-start items-center w-full'>
                {(!isDAR || isRepairOrderRelease || (isDAR && darPrinted)) && (
                    <button
                        className='flex absolute left-8 z-10 justify-center items-center px-3 my-auto w-24 h-10 text-lg text-white rounded-md bg-blue-primary hover:bg-blue-secondary'
                        onClick={handleBackButton}
                    >
                        <ArrowLeftIcon className='mr-2 w-6 h-6' />
                        <span>Back</span>
                    </button>
                )}
                <article className='flex flex-col items-center'>
                    <h1 className='text-4xl font-bold'>
                        Forms for{' '}
                        <a
                            className='text-blue-900 underline hover:text-blue-400'
                            href={currentReleaseLine.invLineLink}
                            target='_blank'
                            rel='noopener noreferrer'
                        >
                            {currentReleaseLine?.invLineName}
                        </a>
                    </h1>
                    <p className='mt-2 text-lg font-bold'>Select Forms to Print</p>
                    {isRepairOrderRelease && currentReleaseLine?.repairOrderLink ? (
                        <p className='mt-2 text-base'>
                            Repair Order:{' '}
                            <a
                                className='font-bold text-blue-900 underline hover:text-blue-400'
                                href={currentReleaseLine.repairOrderLink}
                                target='_blank'
                                rel='noopener noreferrer'
                            >
                                {currentReleaseLine.repairOrderName}
                            </a>
                        </p>
                    ) : null}
                </article>
                {currentReleaseLine?.lineNumber === 1 && currentReleaseLine?.hasSixOrMoreInventoryLines && (
                    <button
                        className={`flex absolute right-8 z-10 justify-center items-center px-3 my-auto h-10 text-lg text-white rounded-md bg-blue-primary hover:bg-blue-secondary ${
                            loading ? 'opacity-60 cursor-not-allowed hover:bg-blue-primary' : ''
                        }`}
                        disabled={loading}
                        onClick={() => setShowBigATAModal(true)}
                    >
                        Generate Big ATA
                    </button>
                )}
            </header>
            {loading ? (
                <section className='flex absolute top-0 left-0 justify-center items-center w-screen h-screen'>
                    <LoadingSpinner className={'h-fit'} />
                </section>
            ) : (
                <section className='w-full flex flex-col items-center justify-center gap-y-8 max-w-[1280px]'>
                    <section className='flex flex-col gap-y-8 w-full'>
                        <article className='flex flex-col gap-2 w-full'>
                            <h1 className='text-lg font-bold'>Required</h1>
                            <article className='flex flex-row p-3 w-full bg-gray-100 rounded-md border border-gray-300'>
                                <div className='w-1/2'>
                                    <h1 className='w-full font-semibold text-left'>Form</h1>
                                </div>
                                <div className='w-1/2'>
                                    <h1 className='w-full font-semibold text-center'>Printed</h1>
                                </div>
                            </article>
                            {requiredDocuments?.length &&
                                requiredDocuments?.map((name, index) => {
                                    return (
                                        <FormRowItem
                                            key={index}
                                            formName={name}
                                            handleDocumentSelection={handleDocumentSelection}
                                            selectedDocuments={selectedDocuments}
                                            dynamicallyParseFormData={dynamicallyParseFormData}
                                            currentForm={currentForm}
                                            formOverrides={formOverrides}
                                            conditionCodes={conditionCodes}
                                            releaseLineName={currentReleaseLine.releaseLineName}
                                        />
                                    );
                                })}
                        </article>
                        <article className='flex flex-col gap-2 w-full'>
                            <h1 className='text-lg font-bold'>Not Required</h1>
                            <article className='flex flex-row p-3 w-full bg-gray-100 rounded-md border border-gray-300'>
                                <div className='w-1/2'>
                                    <h1 className='w-full font-semibold text-left'>Form</h1>
                                </div>
                                <div className='w-1/2'>
                                    <h1 className='w-full font-semibold text-center'>Printed</h1>
                                </div>
                            </article>
                            {nonRequiredDocuments?.length &&
                                nonRequiredDocuments?.map((name, index) => {
                                    return (
                                        <FormRowItem
                                            key={index}
                                            formName={name}
                                            handleDocumentSelection={handleDocumentSelection}
                                            selectedDocuments={selectedDocuments}
                                            dynamicallyParseFormData={dynamicallyParseFormData}
                                            currentForm={currentForm}
                                            formOverrides={formOverrides}
                                            conditionCodes={conditionCodes}
                                            releaseLineName={currentReleaseLine.releaseLineName}
                                        />
                                    );
                                })}
                        </article>
                    </section>
                    <section className='flex flex-col gap-y-8 w-full'>
                        {currentForm ? (
                            <button
                                className='px-3 py-2 w-full text-2xl text-white bg-green-600 rounded-md border hover:bg-green-500 disabled:pointer-events-none'
                                onClick={handleFormPrint}
                                disabled={!currentForm || printButtonLoading}
                            >
                                {printButtonLoading ? (
                                    <div className='flex justify-center items-center'>
                                        <LoadingSpinner className='w-44' spinnerSize={'8'} color={'text-white'} />
                                    </div>
                                ) : (
                                    'Print Form'
                                )}
                            </button>
                        ) : (
                            <button
                                className='px-3 py-2 w-full text-2xl text-white bg-green-600 rounded-md my-2border hover:bg-green-500 disabled:pointer-events-none'
                                onClick={handleSaveInspectionStatus}
                                disabled={
                                    !selectedDocuments.length ||
                                    acceptButtonLoading ||
                                    (isDAR && !isRepairOrderRelease && !darPrinted)
                                }
                            >
                                {acceptButtonLoading ? (
                                    <div className='flex justify-center items-center'>
                                        <LoadingSpinner className='w-44' spinnerSize={'8'} color={'text-white'} />
                                    </div>
                                ) : (
                                    'Accept Forms'
                                )}
                            </button>
                        )}
                    </section>
                </section>
            )}
            <EditableBigAtaTableModal
                isOpen={showBigATAModal}
                title='Big ATA'
                subtitle='Edit rows. Click outside to auto-save.'
                initialRows={ataModalData?.editedRows ?? ataModalData?.releaseNumberData ?? ataModalData?.rows ?? []}
                preferredColumnOrder={[
                    'PartNumber',
                    'Keyword',
                    'ConditionCode',
                    'Quantity',
                    'Trace',
                    'TagAgency',
                    'SerialNumber',
                ]}
                isNumberColumn={(key) => key === 'Quantity'}
                autoSaveOnBackdrop={true}
                onSave={handleSaveBigATA}
                onAutoSave={handleAutoSaveBigATA}
                onCancel={() => setShowBigATAModal(false)}
                accountOptions={accountOptions}
                bigAtaConditionCodes={bigAtaConditionCodes}
            />
            <ConfirmModal
                open={showConfirmPrintBigATA}
                title='Print Big ATA?'
                message='Please confirm to print the Big ATA.'
                onConfirm={handleConfirmPrintYes}
                onCancel={handleConfirmPrintNo}
                isLoading={printButtonLoading}
            />
        </section>
    );
}

function FormRowItem({
    formName,
    handleDocumentSelection,
    selectedDocuments,
    dynamicallyParseFormData,
    currentForm,
    formOverrides,
    conditionCodes,
    releaseLineName,
}) {
    const handleSelection = () => {
        if (currentForm === formName) {
            handleDocumentSelection(null);
            return;
        }
        handleDocumentSelection(formName);
    };

    return (
        <section>
            <section
                className={
                    'flex flex-col w-full border-gray-300 overflow-hidden h cursor-pointer duration-75 transition-colors bg-gray-100 hover:bg-gray-200 ' +
                    (currentForm === formName ? ' rounded-t-md border-x border-t ' : ' border rounded-md')
                }
                onClick={handleSelection}
            >
                <section
                    className={'flex flex-row w-full gap-2 border-gray-300 p-3 ' + (currentForm === formName && 'border-b')}
                >
                    <article className='flex flex-row gap-3 w-1/2 rounded-md'>
                        <ChevronRightIcon
                            className={'h-4 w-4 duration-75 mr-4 ' + (currentForm === formName && 'rotate-90')}
                        />
                        <h1 className='w-full font-semibold text-left'>{documentNames[formName]}</h1>
                    </article>
                    <article className='flex justify-center items-center w-1/2 rounded-md'>
                        <input
                            type='checkbox'
                            readOnly={true}
                            checked={selectedDocuments.some((doc) => {
                                if (
                                    formName === documentReferences.PACKING_SLIP_UNPRICED &&
                                    doc.documentType === documentReferences.PACKING_SLIP_UNPRICED
                                ) {
                                    return doc;
                                }
                                if (
                                    formName === documentReferences.PACKING_SLIP_PRICED &&
                                    doc.documentType === documentReferences.PACKING_SLIP_PRICED
                                ) {
                                    return doc;
                                }
                                if (doc.documentType === formName && doc.releaseLineNumber === releaseLineName) {
                                    return doc;
                                }
                            })}
                        />
                    </article>
                </section>
            </section>
            <form
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                    dynamicallyParseFormData(e, documentKeys[formName]);
                }}
                className={
                    'w-full flex-col justify-between gap-4 px-4 py-5 duration-75 bg-gray-50 rounded-b-md border-x border-b border-gray-300 ' +
                    (currentForm === formName ? 'flex' : 'hidden')
                }
            >
                <FormInputs
                    formName={formName}
                    currentForm={currentForm}
                    formOverrides={formOverrides}
                    conditionCodes={conditionCodes}
                />
            </form>
        </section>
    );
}

function FormInputs({ formName, currentForm, formOverrides, conditionCodes }) {
    if (formName === documentReferences.DOCUMENT_8130) {
        return <Document8130Form documentDefaults={formOverrides} />;
    }

    if (formName === documentReferences.DOCUMENT_ASA) {
        return <ASADocumentForm documentDefaults={formOverrides} conditionCodes={conditionCodes} />;
    }

    if (formName === documentReferences.DOCUMENT_BOEING) {
        return <BoeingDocumentForm documentDefaults={formOverrides} conditionCodes={conditionCodes} />;
    }

    if (formName === documentReferences.PACKING_SLIP_PRICED) {
        return (
            <PackingSlipForm
                formName={documentKeys.PACKING_SLIP_PRICED}
                documentDefaults={currentForm === documentReferences.PACKING_SLIP_PRICED ? formOverrides : {}}
            />
        );
    }

    if (formName === documentReferences.PACKING_SLIP_UNPRICED) {
        return (
            <PackingSlipForm
                formName={documentKeys.PACKING_SLIP_UNPRICED}
                documentDefaults={currentForm === documentReferences.PACKING_SLIP_UNPRICED ? formOverrides : {}}
            />
        );
    }

    return null;
}
